import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronRight, FiLock } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import BulkActions from '../../components/common/BulkActions';
import Modal from '../../components/common/Modal';
import Loader from '../../components/common/Loader';
import SearchableSelect from '../../components/common/SearchableSelect';

export default function AccountStructure() {
  const [activeTab, setActiveTab] = useState('types');
  const [loading, setLoading] = useState(true);

  const [types, setTypes] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [modal, setModal] = useState(null);
  const [modalType, setModalType] = useState('type');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [expandedTypes, setExpandedTypes] = useState({});

  const [filterType, setFilterType] = useState('');
  const [filterSubType, setFilterSubType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [search, setSearch] = useState('');

  const [selectedIds, setSelectedIds] = useState([]);
  const [lockedAccountIds, setLockedAccountIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, subTypesRes, categoriesRes, accountsRes, settingsRes] = await Promise.all([
        api.accounts.types(),
        api.accounts.subTypes(),
        api.accounts.categories(),
        api.accounts.list({ per_page: 500, operation: 'view-accounts' }),
        api.settings.get().catch(() => ({ data: {} })),
      ]);
      setTypes(typesRes.data.types || []);
      setSubTypes(subTypesRes.data.sub_types || []);
      setCategories(categoriesRes.data.categories || []);
      const accData = accountsRes.data.accounts;
      setAccounts(Array.isArray(accData) ? accData : (accData?.data || []));

      // Build locked account IDs from settings
      const s = settingsRes.data?.settings || settingsRes.data || {};
      const keys = [
        'cash_account_id', 'default_bank_account_id', 'sales_account_id',
        'sales_return_account_id', 'commission_account_id', 'tax_payable_account_id',
        'wht_payable_account_id', 'wht_receivable_account_id', 'salary_expense_account_id',
        'deductions_payable_account_id', 'employee_advance_account_id',
        'purchase_expense_account_id', 'input_gst_account_id', 'output_gst_account_id',
      ];
      const locked = new Set();
      keys.forEach(k => { const v = parseInt(s[k]); if (v) locked.add(v); });
      setLockedAccountIds(locked);
    } catch (err) {
      showError('Failed to load data', extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleType = (id) => setExpandedTypes((p) => ({ ...p, [id]: !p[id] }));

  const openAdd = (type) => {
    setModalType(type);
    if (type === 'type') {
      setForm({ name: '', account_nature: 'debit', is_active: true });
    } else if (type === 'sub_type') {
      setForm({ name: '', type_id: '', account_nature: 'debit', is_active: true });
    } else if (type === 'category') {
      setForm({ name: '', type_id: '', sub_type_id: '', account_nature: 'debit', is_active: true });
    } else {
      setForm({ name: '', type_id: '', sub_type_id: '', category_id: '', account_nature: 'debit', opening_balance: 0, is_active: true });
    }
    setErrors({});
    setModal('add');
  };

  const openEdit = (item, type) => {
    setModalType(type);
    if (type === 'type') {
      setForm({ id: item.id, name: item.name, account_nature: item.account_nature || 'debit', is_active: item.is_active !== false });
    } else if (type === 'sub_type') {
      setForm({ id: item.id, name: item.name, type_id: item.type_id, account_nature: item.account_nature || 'debit', is_active: item.is_active !== false });
    } else if (type === 'category') {
      const parentSubType = subTypes.find(s => s.id === item.sub_type_id);
      setForm({ id: item.id, name: item.name, type_id: parentSubType?.type_id || '', sub_type_id: item.sub_type_id, account_nature: item.account_nature || 'debit', is_active: item.is_active !== false });
    } else {
      setForm({ id: item.id, name: item.name, type_id: item.type_id, sub_type_id: item.sub_type_id, category_id: item.category_id, account_nature: item.account_nature || 'debit', opening_balance: item.opening_balance || 0, is_active: item.is_active !== false });
    }
    setErrors({});
    setModal('edit');
  };

  const save = async () => {
    const errs = {};
    if (!form.name) errs.name = 'Name is required';
    if (modalType === 'sub_type' && !form.type_id) errs.type_id = 'Type is required';
    if (modalType === 'category' && !form.sub_type_id) errs.sub_type_id = 'Sub Type is required';
    if (modalType === 'account') {
      if (!form.type_id) errs.type_id = 'Type is required';
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const { id, ...payload } = form;
      if (payload.opening_balance !== undefined && payload.opening_balance !== '' && payload.opening_balance !== null) {
        payload.opening_balance = parseFloat(payload.opening_balance);
      }
      if (modalType === 'type') {
        if (id) await api.accounts.updateType(id, payload);
        else await api.accounts.createType(payload);
      } else if (modalType === 'sub_type') {
        if (id) await api.accounts.updateSubType(id, payload);
        else await api.accounts.createSubType(payload);
      } else if (modalType === 'category') {
        if (id) await api.accounts.updateCategory(id, payload);
        else await api.accounts.createCategory(payload);
      } else {
        if (id) await api.accounts.update(id, payload);
        else await api.accounts.create(payload);
      }
      toast('success', '', 'Saved successfully');
      setModal(null);
      load();
    } catch (err) {
      showError('Failed to save', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name, type) => {
    if (type === 'account' && lockedAccountIds.has(id)) {
      showError('This account is locked in Settings > Accounting Accounts. Please change it there first before deleting.');
      return;
    }
    const res = await confirmDelete(`"${name}"`);
    if (!res.isConfirmed) return;
    try {
      if (type === 'type') await api.accounts.removeType(id);
      else if (type === 'sub_type') await api.accounts.removeSubType(id);
      else if (type === 'category') await api.accounts.removeCategory(id);
      else await api.accounts.remove(id);
      toast('success', '', 'Deleted');
      load();
    } catch (err) {
      showError('Failed to delete', extractError(err));
    }
  };

  const bulkTypeMap = { type: 'account_types', sub_type: 'account_sub_types', category: 'account_categories', account: 'accounts' };

  const bulkRemove = async (ids, tabType) => {
    const res = await confirmDelete(`${ids.length} ${tabType === 'account' ? 'accounts' : tabType + 's'}`);
    if (!res.isConfirmed) return;
    try {
      await api.bulkDelete({ type: bulkTypeMap[tabType], ids });
      toast('success', '', 'Deleted');
      setSelectedIds([]);
      load();
    } catch (err) {
      showError('Failed to delete', extractError(err));
    }
  };

  const tabs = [
    { key: 'types', label: 'Account Types' },
    { key: 'sub_types', label: 'Sub Types' },
    { key: 'categories', label: 'Categories' },
    { key: 'accounts', label: 'Accounts' },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Account Structure"
        subtitle="Manage account types, sub types, categories and accounts."
        crumbs={[{ label: 'Finance' }, { label: 'Account Structure' }]}
      />

      <div className="card">
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 16px' }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontWeight: activeTab === tab.key ? 600 : 400,
                color: activeTab === tab.key ? 'var(--primary)' : 'var(--text-muted)',
                borderBottom: activeTab === tab.key ? '2px solid var(--primary)' : '2px solid transparent',
                fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 16 }}>
          {loading ? (
            <Loader text="Loading..." />
          ) : (
            <>
              {activeTab === 'types' && (
                <TypesTab types={types} subTypes={subTypes} accounts={accounts} expandedTypes={expandedTypes} toggleType={toggleType} onEdit={openEdit} onDelete={remove} onAdd={openAdd} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onBulkDelete={(ids) => bulkRemove(ids, 'type')} />
              )}
              {activeTab === 'sub_types' && (
                <SubTypesTab types={types} subTypes={subTypes} categories={categories} accounts={accounts} filterType={filterType} setFilterType={setFilterType} onEdit={openEdit} onDelete={remove} onAdd={openAdd} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onBulkDelete={(ids) => bulkRemove(ids, 'sub_type')} />
              )}
              {activeTab === 'categories' && (
                <CategoriesTab types={types} subTypes={subTypes} categories={categories} accounts={accounts} filterType={filterType} setFilterType={setFilterType} filterSubType={filterSubType} setFilterSubType={setFilterSubType} onEdit={openEdit} onDelete={remove} onAdd={openAdd} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onBulkDelete={(ids) => bulkRemove(ids, 'category')} />
              )}
              {activeTab === 'accounts' && (
                <AccountsTab types={types} subTypes={subTypes} categories={categories} accounts={accounts} lockedAccountIds={lockedAccountIds} filterType={filterType} setFilterType={setFilterType} filterSubType={filterSubType} setFilterSubType={setFilterSubType} filterCategory={filterCategory} setFilterCategory={setFilterCategory} search={search} setSearch={setSearch} onEdit={openEdit} onDelete={remove} onAdd={openAdd} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onBulkDelete={(ids) => bulkRemove(ids, 'account')} />
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal === 'edit'
          ? `Edit ${modalType === 'type' ? 'Type' : modalType === 'sub_type' ? 'Sub Type' : modalType === 'category' ? 'Category' : 'Account'}`
          : `Add ${modalType === 'type' ? 'Type' : modalType === 'sub_type' ? 'Sub Type' : modalType === 'category' ? 'Category' : 'Account'}`}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Name <span className="req">*</span></label>
          <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name || ''} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        </div>

        {(modalType === 'sub_type' || modalType === 'category' || modalType === 'account') && (
          <div className="form-group">
            <label className="form-label">Type <span className="req">*</span></label>
            <SearchableSelect
              options={types.map(t => ({ value: t.id, label: t.name }))}
              value={form.type_id}
              onChange={(v) => {
                if (modalType === 'account') setForm({ ...form, type_id: v, sub_type_id: null, category_id: null });
                else if (modalType === 'category') setForm({ ...form, type_id: v, sub_type_id: null });
                else setForm({ ...form, type_id: v });
              }}
              placeholder="-- Select Type --"
            />
            {errors.type_id && <div className="invalid-feedback">{errors.type_id}</div>}
          </div>
        )}

        {(modalType === 'category' || modalType === 'account') && (
          <div className="form-group">
            <label className="form-label">Sub Type {modalType === 'category' && <span className="req">*</span>}</label>
            <SearchableSelect
              options={subTypes.filter(st => st.type_id === form.type_id).map(st => ({ value: st.id, label: st.name }))}
              value={form.sub_type_id}
              onChange={(v) => modalType === 'account'
                ? setForm({ ...form, sub_type_id: v, category_id: null })
                : setForm({ ...form, sub_type_id: v })}
              placeholder="-- Select Sub Type --"
            />
            {errors.sub_type_id && <div className="invalid-feedback">{errors.sub_type_id}</div>}
          </div>
        )}

        {modalType === 'account' && (
          <>
            <div className="form-group">
              <label className="form-label">Category</label>
              <SearchableSelect
                options={categories.filter(c => c.sub_type_id === form.sub_type_id).map(c => ({ value: c.id, label: c.name }))}
                value={form.category_id}
                onChange={(v) => setForm({ ...form, category_id: v })}
                placeholder="-- Select Category --"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Account Nature</label>
              <SearchableSelect options={[{ value: 'debit', label: 'Debit' }, { value: 'credit', label: 'Credit' }]} value={form.account_nature} onChange={(v) => setForm({ ...form, account_nature: v })} placeholder="-- Select --" />
            </div>
            <div className="form-group">
              <label className="form-label">Opening Balance</label>
              <input type="number" step="0.01" className="form-control" value={form.opening_balance ?? 0} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
            </div>
          </>
        )}

        {(modalType === 'type' || modalType === 'sub_type' || modalType === 'category') && (
          <div className="form-group">
            <label className="form-label">Account Nature</label>
            <SearchableSelect options={[{ value: 'debit', label: 'Debit' }, { value: 'credit', label: 'Credit' }]} value={form.account_nature} onChange={(v) => setForm({ ...form, account_nature: v })} placeholder="-- Select --" />
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, marginTop: 8 }}>
          <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          Active
        </label>
      </Modal>
    </div>
  );
}

function TypesTab({ types, subTypes, accounts, expandedTypes, toggleType, onEdit, onDelete, onAdd, selectedIds, setSelectedIds, onBulkDelete }) {
  const allIds = types.map(t => t.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : allIds);
  const toggleOne = (id) => setSelectedIds(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        {selectedIds.length > 0 && <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={() => {}} deleteType="account_types" label="account types" />}
        <button className="btn btn-primary btn-sm" onClick={() => onAdd('type')}><FiPlus /> Add Type</button>
      </div>
      {types.map((type) => {
        const typeSubTypes = subTypes.filter(st => st.type_id === type.id);
        const typeAccounts = accounts.filter(a => a.type_id === type.id && !a.sub_type_id);
        const isOpen = expandedTypes[type.id];
        return (
          <div key={type.id} style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8 }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: 'pointer', background: 'var(--bg-secondary)', borderRadius: 8 }}
              onClick={() => toggleType(type.id)}
            >
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{isOpen ? <FiChevronDown /> : <FiChevronRight />}</span>
              <span style={{ fontWeight: 600, flex: 1 }}>{type.name}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{type.account_nature}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg)', padding: '2px 8px', borderRadius: 4 }}>{typeSubTypes.length} sub types</span>
              <input type="checkbox" checked={selectedIds.includes(type.id)} onChange={() => toggleOne(type.id)} onClick={(e) => e.stopPropagation()} />
              <button className="btn-icon" title="Edit" onClick={(e) => { e.stopPropagation(); onEdit(type, 'type'); }}><FiEdit2 /></button>
              <button className="btn-icon danger" title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(type.id, type.name, 'type'); }}><FiTrash2 /></button>
            </div>
            {isOpen && (
              <div style={{ padding: '8px 12px 8px 32px' }}>
                {typeSubTypes.length === 0 && typeAccounts.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No sub types yet</div>
                ) : (
                  <>
                    {typeSubTypes.map(st => (
                      <div key={st.id} style={{ padding: '4px 0', fontSize: 13 }}>• {st.name} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({st.account_nature})</span></div>
                    ))}
                    {typeAccounts.map(acc => (
                      <div key={acc.id} style={{ padding: '4px 0', fontSize: 13, color: 'var(--text-secondary)' }}>– {acc.name} <span style={{ fontSize: 11 }}>(account)</span></div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SubTypesTab({ types, subTypes, categories, accounts, filterType, setFilterType, onEdit, onDelete, onAdd, selectedIds, setSelectedIds, onBulkDelete }) {
  const filtered = filterType ? subTypes.filter(st => st.type_id === filterType) : subTypes;
  const allIds = filtered.map(s => s.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : allIds);
  const toggleOne = (id) => setSelectedIds(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
          <SearchableSelect
            options={[{ value: '', label: 'All Types' }, ...types.map(t => ({ value: t.id, label: t.name }))]}
            value={filterType}
            onChange={setFilterType}
            placeholder="Filter by Type"
            allowClear
          />
          {selectedIds.length > 0 && <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={() => {}} deleteType="account_sub_types" label="sub types" />}
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => onAdd('sub_type')}><FiPlus /> Add Sub Type</button>
      </div>
      <table className="table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
            <th>Name</th>
            <th>Type</th>
            <th>Nature</th>
            <th>Categories</th>
            <th>Accounts</th>
            <th style={{ width: 80 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((st) => {
            const parentType = types.find(t => t.id === st.type_id);
            const stCategories = categories.filter(c => c.sub_type_id === st.id);
            const stAccounts = accounts.filter(a => a.sub_type_id === st.id && !a.category_id);
            return (
              <tr key={st.id} style={selectedIds.includes(st.id) ? { backgroundColor: 'var(--primary-bg, #eff6ff)' } : {}}>
                <td><input type="checkbox" checked={selectedIds.includes(st.id)} onChange={() => toggleOne(st.id)} /></td>
                <td style={{ fontWeight: 500 }}>{st.name}</td>
                <td><span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{parentType?.name || '-'}</span></td>
                <td><span style={{ fontSize: 12 }}>{st.account_nature}</span></td>
                <td><span style={{ fontSize: 12 }}>{stCategories.length}</span></td>
                <td><span style={{ fontSize: 12 }}>{stAccounts.length}</span></td>
                <td>
                  <button className="btn-icon" title="Edit" onClick={() => onEdit(st, 'sub_type')}><FiEdit2 /></button>
                  <button className="btn-icon danger" title="Delete" onClick={() => onDelete(st.id, st.name, 'sub_type')}><FiTrash2 /></button>
                </td>
              </tr>
            );
          })}
          {filtered.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No sub types found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CategoriesTab({ types, subTypes, categories, accounts, filterType, setFilterType, filterSubType, setFilterSubType, onEdit, onDelete, onAdd, selectedIds, setSelectedIds, onBulkDelete }) {
  const filtered = categories.filter(c => {
    if (filterType) {
      const st = subTypes.find(s => s.id === c.sub_type_id);
      if (!st || st.type_id !== filterType) return false;
    }
    if (filterSubType && c.sub_type_id !== filterSubType) return false;
    return true;
  });
  const allIds = filtered.map(c => c.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : allIds);
  const toggleOne = (id) => setSelectedIds(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 12, flex: 1, alignItems: 'center' }}>
          <SearchableSelect
            options={[{ value: '', label: 'All Types' }, ...types.map(t => ({ value: t.id, label: t.name }))]}
            value={filterType}
            onChange={(v) => { setFilterType(v); setFilterSubType(''); }}
            placeholder="Filter by Type"
            allowClear
          />
          <SearchableSelect
            options={[{ value: '', label: 'All Sub Types' }, ...subTypes.filter(st => !filterType || st.type_id === filterType).map(st => ({ value: st.id, label: st.name }))]}
            value={filterSubType}
            onChange={setFilterSubType}
            placeholder="Filter by Sub Type"
            allowClear
          />
          {selectedIds.length > 0 && <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={() => {}} deleteType="account_categories" label="categories" />}
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => onAdd('category')}><FiPlus /> Add Category</button>
      </div>
      <table className="table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
            <th>Name</th>
            <th>Sub Type</th>
            <th>Type</th>
            <th>Nature</th>
            <th>Accounts</th>
            <th style={{ width: 80 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((cat) => {
            const parentSubType = subTypes.find(s => s.id === cat.sub_type_id);
            const parentType = parentSubType ? types.find(t => t.id === parentSubType.type_id) : null;
            const catAccounts = accounts.filter(a => a.category_id === cat.id);
            return (
              <tr key={cat.id} style={selectedIds.includes(cat.id) ? { backgroundColor: 'var(--primary-bg, #eff6ff)' } : {}}>
                <td><input type="checkbox" checked={selectedIds.includes(cat.id)} onChange={() => toggleOne(cat.id)} /></td>
                <td style={{ fontWeight: 500 }}>{cat.name}</td>
                <td><span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{parentSubType?.name || '-'}</span></td>
                <td><span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{parentType?.name || '-'}</span></td>
                <td><span style={{ fontSize: 12 }}>{cat.account_nature}</span></td>
                <td><span style={{ fontSize: 12 }}>{catAccounts.length}</span></td>
                <td>
                  <button className="btn-icon" title="Edit" onClick={() => onEdit(cat, 'category')}><FiEdit2 /></button>
                  <button className="btn-icon danger" title="Delete" onClick={() => onDelete(cat.id, cat.name, 'category')}><FiTrash2 /></button>
                </td>
              </tr>
            );
          })}
          {filtered.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No categories found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AccountsTab({ types, subTypes, categories, accounts, lockedAccountIds, filterType, setFilterType, filterSubType, setFilterSubType, filterCategory, setFilterCategory, search, setSearch, onEdit, onDelete, onAdd, selectedIds, setSelectedIds, onBulkDelete }) {

  const filtered = accounts.filter(a => {
    if (filterType && a.type_id !== filterType) return false;
    if (filterSubType && a.sub_type_id !== filterSubType) return false;
    if (filterCategory && a.category_id !== filterCategory) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const allIds = filtered.map(a => a.id);
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : allIds);
  const toggleOne = (id) => setSelectedIds(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 8 }}>
        <input
          className="form-control"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 140, flexShrink: 0 }}
        />
        <div style={{ width: 140, flexShrink: 0 }}>
          <SearchableSelect
            options={[{ value: '', label: 'All Types' }, ...types.map(t => ({ value: t.id, label: t.name }))]}
            value={filterType}
            onChange={(v) => { setFilterType(v); setFilterSubType(''); setFilterCategory(''); }}
            placeholder="Type"
            allowClear
          />
        </div>
        <div style={{ width: 140, flexShrink: 0 }}>
          <SearchableSelect
            options={[{ value: '', label: 'All Sub Types' }, ...subTypes.filter(st => !filterType || st.type_id === filterType).map(st => ({ value: st.id, label: st.name }))]}
            value={filterSubType}
            onChange={(v) => { setFilterSubType(v); setFilterCategory(''); }}
            placeholder="Sub Type"
            allowClear
          />
        </div>
        <div style={{ width: 140, flexShrink: 0 }}>
          <SearchableSelect
            options={[{ value: '', label: 'All Categories' }, ...categories.filter(c => !filterSubType || c.sub_type_id === filterSubType).map(c => ({ value: c.id, label: c.name }))]}
            value={filterCategory}
            onChange={setFilterCategory}
            placeholder="Category"
            allowClear
          />
        </div>
        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <button className="btn btn-primary btn-sm" onClick={() => onAdd('account')}><FiPlus /> Add Account</button>
        </div>
      </div>
      {selectedIds.length > 0 && <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={() => {}} deleteType="accounts" label="accounts" />}
      <table className="table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
            <th>Name</th>
            <th>Type</th>
            <th>Sub Type</th>
            <th>Category</th>
            <th>Nature</th>
            <th>Balance</th>
            <th style={{ width: 80 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((acc) => {
            const parentType = types.find(t => t.id === acc.type_id);
            const parentSubType = subTypes.find(s => s.id === acc.sub_type_id);
            const parentCategory = categories.find(c => c.id === acc.category_id);
            return (
              <tr key={acc.id} style={selectedIds.includes(acc.id) ? { backgroundColor: 'var(--primary-bg, #eff6ff)' } : {}}>
                <td><input type="checkbox" checked={selectedIds.includes(acc.id)} onChange={() => toggleOne(acc.id)} /></td>
                <td style={{ fontWeight: 500 }}>
                  {acc.name}
                  {lockedAccountIds.has(acc.id) && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--warning, #f59e0b)', display: 'inline-flex', alignItems: 'center', gap: 2 }} title="Locked — managed in Settings > Accounting Accounts">
                      <FiLock size={11} /> Locked
                    </span>
                  )}
                </td>
                <td><span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{parentType?.name || '-'}</span></td>
                <td><span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{parentSubType?.name || '-'}</span></td>
                <td><span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{parentCategory?.name || '-'}</span></td>
                <td><span style={{ fontSize: 12 }}>{acc.account_nature}</span></td>
                <td><span style={{ fontSize: 12 }}>{Number(acc.opening_balance || 0).toLocaleString()}</span></td>
                <td>
                  <button className="btn-icon" title="Edit" onClick={() => onEdit(acc, 'account')}><FiEdit2 /></button>
                  {!lockedAccountIds.has(acc.id) ? (
                    <button className="btn-icon danger" title="Delete" onClick={() => onDelete(acc.id, acc.name, 'account')}><FiTrash2 /></button>
                  ) : (
                    <button className="btn-icon" title="Locked — change in Settings > Accounting Accounts" style={{ opacity: 0.3, cursor: 'not-allowed' }}><FiLock /></button>
                  )}
                </td>
              </tr>
            );
          })}
          {filtered.length === 0 && (
            <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No accounts found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
